import { studentCourseConstants } from '../actions'

const initialState = {
    // Course group overview (list of all course groups)
    fetchingCourseGroupOverview: false,
    courseGroupOverview: null,

    // Map of courseGroupId -> CourseGroupV1Dto (for loading multiple course groups at once)
    courseGroupDetailsMap: {},
    fetchingCourseGroupDetailsMap: {},

    // Individual course group content (for single detail view / course menu)
    fetchingCourseGroup: false,
    currentCourseGroup: null,
    currentCourseGroupId: null,
    currentL1Id: null,

    // Course group progression
    fetchingCourseGroupProgression: false,
    courseGroupProgression: null,

    // Map of courseGroupId -> CourseGroupProgressionV1Dto (for loading multiple progressions at once)
    courseGroupProgressionMap: {},
    fetchingCourseGroupProgressionMap: {},

    // Setting L1 language
    settingL1: false,
    courseGroupMetadata: null,

    // Course lesson order (topic-level lesson list)
    fetchingCourseLessonOrder: false,
    courseLessonOrder: null,
    currentTopicId: null,

    // Course lesson order progression (topic-level progression)
    fetchingCourseLessonOrderProgression: false,
    courseLessonOrderProgression: null,

    // Individual lesson (scenario)
    fetchingLesson: false,
    currentLesson: null,
    currentCustomLessonId: null,

    // Lesson report
    reportingLesson: false,
    lessonReportResult: null,

    // Image cache: imageUrl -> blobUrl
    imageCache: {},
    fetchingImages: {},

    // Audio cache: audioUrl -> blobUrl
    audioCache: {},
    fetchingAudio: {},

    // Main menu selection state (persisted across navigation)
    selectedCourseGroupId: null,
    selectedTopicId: null,
}

export function studentCourse(state = initialState, action) {
    switch (action.type) {
        // ─── Set L1 Language ──────────────────────────────────────
        case studentCourseConstants.STUDENT_COURSE_GROUP_SET_L1.REQUEST:
            return {
                ...state,
                settingL1: true,
            }
        case studentCourseConstants.STUDENT_COURSE_GROUP_SET_L1.SUCCESS: {
            const updatedMetadata = action.data
            const cgId = updatedMetadata?.courseGroupId

            // Clear cached details and progression so they get refetched with the new L1
            const newDetailsMap = { ...state.courseGroupDetailsMap }
            const newProgressionMap = { ...state.courseGroupProgressionMap }
            if (cgId) {
                delete newDetailsMap[cgId]
                delete newProgressionMap[cgId]
            }

            // Update the metadata entry inside the overview so the UI reflects the new L1
            let updatedOverview = state.courseGroupOverview
            if (updatedOverview && updatedMetadata && cgId) {
                updatedOverview = {
                    ...updatedOverview,
                    courseGroupMetadataV2Dtos: updatedOverview.courseGroupMetadataV2Dtos.map((m) =>
                        m.courseGroupId === cgId ? updatedMetadata : m
                    ),
                }
            }

            return {
                ...state,
                settingL1: false,
                courseGroupMetadata: updatedMetadata,
                currentL1Id: updatedMetadata?.l1Id ?? state.currentL1Id,
                courseGroupDetailsMap: newDetailsMap,
                courseGroupProgressionMap: newProgressionMap,
                courseGroupOverview: updatedOverview,
            }
        }
        case studentCourseConstants.STUDENT_COURSE_GROUP_SET_L1.FAILURE:
            return {
                ...state,
                settingL1: false,
            }

        // ─── Course Group Overview ────────────────────────────────
        case studentCourseConstants.STUDENT_COURSE_GROUP_GET_OVERVIEW.REQUEST:
            return {
                ...state,
                fetchingCourseGroupOverview: true,
            }
        case studentCourseConstants.STUDENT_COURSE_GROUP_GET_OVERVIEW.SUCCESS:
            return {
                ...state,
                fetchingCourseGroupOverview: false,
                courseGroupOverview: action.data,
            }
        case studentCourseConstants.STUDENT_COURSE_GROUP_GET_OVERVIEW.FAILURE:
            return {
                ...state,
                fetchingCourseGroupOverview: false,
            }

        // ─── Course Group By ID ───────────────────────────────────
        case studentCourseConstants.STUDENT_COURSE_GROUP_GET_BY_ID.REQUEST:
            return {
                ...state,
                fetchingCourseGroup: true,
                currentCourseGroupId: action.payload.courseGroupId,
                currentL1Id: action.payload.l1Id,
                fetchingCourseGroupDetailsMap: {
                    ...state.fetchingCourseGroupDetailsMap,
                    [action.payload.courseGroupId]: true,
                },
            }
        case studentCourseConstants.STUDENT_COURSE_GROUP_GET_BY_ID.SUCCESS:
            return {
                ...state,
                fetchingCourseGroup: false,
                currentCourseGroup: action.data,
                courseGroupDetailsMap: {
                    ...state.courseGroupDetailsMap,
                    [action.data.id]: action.data,
                },
                fetchingCourseGroupDetailsMap: {
                    ...state.fetchingCourseGroupDetailsMap,
                    [action.data.id]: false,
                },
            }
        case studentCourseConstants.STUDENT_COURSE_GROUP_GET_BY_ID.FAILURE:
            return {
                ...state,
                fetchingCourseGroup: false,
                fetchingCourseGroupDetailsMap: {
                    ...state.fetchingCourseGroupDetailsMap,
                    [action.courseGroupId]: false,
                },
            }

        // ─── Course Group Progression ─────────────────────────────
        case studentCourseConstants.STUDENT_COURSE_GROUP_GET_PROGRESSION.REQUEST:
            return {
                ...state,
                fetchingCourseGroupProgression: true,
                fetchingCourseGroupProgressionMap: {
                    ...state.fetchingCourseGroupProgressionMap,
                    [action.payload.courseGroupId]: true,
                },
            }
        case studentCourseConstants.STUDENT_COURSE_GROUP_GET_PROGRESSION.SUCCESS:
            return {
                ...state,
                fetchingCourseGroupProgression: false,
                courseGroupProgression: action.data,
                courseGroupProgressionMap: {
                    ...state.courseGroupProgressionMap,
                    [action.data.id]: action.data,
                },
                fetchingCourseGroupProgressionMap: {
                    ...state.fetchingCourseGroupProgressionMap,
                    [action.data.id]: false,
                },
            }
        case studentCourseConstants.STUDENT_COURSE_GROUP_GET_PROGRESSION.FAILURE:
            return {
                ...state,
                fetchingCourseGroupProgression: false,
                fetchingCourseGroupProgressionMap: {
                    ...state.fetchingCourseGroupProgressionMap,
                    [action.courseGroupId]: false,
                },
            }

        // ─── Course Lesson Order ──────────────────────────────────
        case studentCourseConstants.STUDENT_COURSE_GET_LESSON_ORDER.REQUEST:
            return {
                ...state,
                fetchingCourseLessonOrder: true,
                currentTopicId: action.payload.topicId,
            }
        case studentCourseConstants.STUDENT_COURSE_GET_LESSON_ORDER.SUCCESS:
            return {
                ...state,
                fetchingCourseLessonOrder: false,
                courseLessonOrder: action.data,
            }
        case studentCourseConstants.STUDENT_COURSE_GET_LESSON_ORDER.FAILURE:
            return {
                ...state,
                fetchingCourseLessonOrder: false,
            }

        // ─── Course Lesson Order Progression ──────────────────────
        case studentCourseConstants.STUDENT_COURSE_GET_LESSON_ORDER_PROGRESSION.REQUEST:
            return {
                ...state,
                fetchingCourseLessonOrderProgression: true,
            }
        case studentCourseConstants.STUDENT_COURSE_GET_LESSON_ORDER_PROGRESSION.SUCCESS:
            return {
                ...state,
                fetchingCourseLessonOrderProgression: false,
                courseLessonOrderProgression: action.data,
            }
        case studentCourseConstants.STUDENT_COURSE_GET_LESSON_ORDER_PROGRESSION.FAILURE:
            return {
                ...state,
                fetchingCourseLessonOrderProgression: false,
            }

        // ─── Get Lesson (Scenario) ────────────────────────────────
        case studentCourseConstants.STUDENT_COURSE_GET_LESSON.REQUEST:
            return {
                ...state,
                fetchingLesson: true,
                currentCustomLessonId: action.payload.customLessonId,
            }
        case studentCourseConstants.STUDENT_COURSE_GET_LESSON.SUCCESS:
            return {
                ...state,
                fetchingLesson: false,
                currentLesson: action.data,
            }
        case studentCourseConstants.STUDENT_COURSE_GET_LESSON.FAILURE:
            return {
                ...state,
                fetchingLesson: false,
            }

        // ─── Report Lesson ────────────────────────────────────────
        case studentCourseConstants.STUDENT_COURSE_REPORT_LESSON.REQUEST:
            return {
                ...state,
                reportingLesson: true,
            }
        case studentCourseConstants.STUDENT_COURSE_REPORT_LESSON.SUCCESS:
            return {
                ...state,
                reportingLesson: false,
                lessonReportResult: action.data,
            }
        case studentCourseConstants.STUDENT_COURSE_REPORT_LESSON.FAILURE:
            return {
                ...state,
                reportingLesson: false,
            }

        // ─── Fetch Image ──────────────────────────────────────────
        case studentCourseConstants.STUDENT_COURSE_FETCH_IMAGE.REQUEST:
            return {
                ...state,
                fetchingImages: {
                    ...state.fetchingImages,
                    [action.payload.imageUrl]: true,
                },
            }
        case studentCourseConstants.STUDENT_COURSE_FETCH_IMAGE.SUCCESS:
            return {
                ...state,
                imageCache: {
                    ...state.imageCache,
                    [action.imageUrl]: action.blobUrl,
                },
                fetchingImages: {
                    ...state.fetchingImages,
                    [action.imageUrl]: false,
                },
            }
        case studentCourseConstants.STUDENT_COURSE_FETCH_IMAGE.FAILURE:
            return {
                ...state,
                fetchingImages: {
                    ...state.fetchingImages,
                    [action.imageUrl]: false,
                },
            }

        // ─── Fetch Audio ──────────────────────────────────────────
        case studentCourseConstants.STUDENT_COURSE_FETCH_AUDIO.REQUEST:
            return {
                ...state,
                fetchingAudio: {
                    ...state.fetchingAudio,
                    [action.payload.audioUrl]: true,
                },
            }
        case studentCourseConstants.STUDENT_COURSE_FETCH_AUDIO.SUCCESS:
            return {
                ...state,
                audioCache: {
                    ...state.audioCache,
                    [action.audioUrl]: action.blobUrl,
                },
                fetchingAudio: {
                    ...state.fetchingAudio,
                    [action.audioUrl]: false,
                },
            }
        case studentCourseConstants.STUDENT_COURSE_FETCH_AUDIO.FAILURE:
            return {
                ...state,
                fetchingAudio: {
                    ...state.fetchingAudio,
                    [action.audioUrl]: false,
                },
            }

        // ─── Main Menu Selection State ─────────────────────────────
        case studentCourseConstants.STUDENT_COURSE_SET_SELECTED_COURSE_GROUP_ID:
            return {
                ...state,
                selectedCourseGroupId: action.payload.courseGroupId,
            }
        case studentCourseConstants.STUDENT_COURSE_SET_SELECTED_TOPIC_ID:
            return {
                ...state,
                selectedTopicId: action.payload.topicId,
            }

        default:
            return state
    }
}
